import { AlertTriangle, Clock, Eye, ShieldCheck } from 'lucide-react';
import { useMemo } from 'react';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatSessionDateTime } from '../../sessionDateTime';
import { formatMobile } from '../../sessionFieldFormat';
import { displayApplicantName, displayReferenceId } from '../../sessionReference';
import { verifiedStatusLabel } from '../statusAxisBadges';
import { Accordion, KeyValueList, KeyValueRow, SubCard, TwoColumnGrid } from './Accordion';
import {
  categoryForStep,
  flagIsOk,
  parseResult,
  resolveFields,
  stepReason,
} from './returnDataCatalog';

// Created / Submitted / Decided At are all MOMENTS, so they carry the time as well as the
// date. Showing only the date hid the one thing an admin actually needs when two sessions
// land on the same day — which came first — and made "Created 14 Aug, Submitted 14 Aug" look
// like a rounding error rather than a real sequence.

/**
 * The run's warnings, DERIVED from stored results rather than from a "warnings" field —
 * we do not have one.
 *
 * Two honest sources:
 *   • a `flag`-display check whose value is on the bad side (`flagIsOk` decides which
 *     side that is per field, since some flags are good when true and some when false);
 *   • the `verification_reason` the rule engine recorded for a step that did not pass.
 *
 * Each warning is labelled with its step, because "Duplicate PAN Flag" means nothing
 * without knowing which check produced it.
 *
 * It used to list `step.riskOutcome` as well. That was wrong: riskOutcome holds the verdict
 * WORD ("Approve" / "Review" / "Reject"), so a cleanly passing step produced the warning
 * "Approve — PAN Verification". `stepReason` reads the actual reason text instead, and
 * returns nothing for a passed step.
 */
function collectWarnings(steps: SessionStepDto[]): { key: string; text: string }[] {
  const out: { key: string; text: string }[] = [];

  for (const step of steps) {
    const spec = categoryForStep(step);
    if (spec) {
      const result = parseResult(step.resultJson);
      for (const rf of resolveFields(spec, result, 'checks')) {
        if (rf.field.display !== 'flag') continue;
        if (flagIsOk(rf.field, rf.raw)) continue;
        out.push({
          key: `${step.id}:${rf.field.key}`,
          text: `${rf.field.label} — ${step.displayName}`,
        });
      }
    }

    const reason = stepReason(step);
    if (reason) {
      out.push({ key: `${step.id}:reason`, text: `${reason} — ${step.displayName}` });
    }
  }

  return out;
}

/**
 * The design's first accordion: Warnings beside Session Details.
 *
 * One deliberate omission — the design's "IP Address and Location" row. We do not record
 * the applicant's IP or geolocation anywhere in the session, so the row would either be a
 * permanent "NA" or, worse, invite someone to fill it with a guess. Left out until there
 * is something real behind it.
 */
export function OverviewAccordion({ session }: { session: SessionDetailDto }) {
  const warnings = useMemo(() => collectWarnings(session.steps), [session.steps]);
  // Whether anything has actually been checked. Distinguishes a run that came back clean
  // from one that has not started — both have zero warnings, and only the first is good news.
  const evaluated = useMemo(
    () => session.steps.some((s) => s.status !== 'Pending'),
    [session.steps],
  );

  return (
    <Accordion icon={<Eye size={18} />} title="Overview" defaultOpen>
      <TwoColumnGrid>
        <WarningsCard warnings={warnings} evaluated={evaluated} />

        <SubCard title="Session Details">
          <KeyValueList>
            <KeyValueRow label="Workflow:" value={session.workflowName} />
            {/* The session's Reference ID — the admin's typed Session Id when there is one,
                otherwise the id minted at creation. Always shown: it is the identifier a
                support conversation can quote, so it beats an NA. NA here means the row
                genuinely stores nothing, which no creation path produces. */}
            <KeyValueRow
              label="Session Id:"
              value={displayReferenceId(session.referenceId) ?? 'NA'}
            />
            <KeyValueRow label="Created:" value={formatSessionDateTime(session.createdAt)} />
            <KeyValueRow label="Submitted:" value={formatSessionDateTime(session.submittedAt)} />
            <KeyValueRow label="Name:" value={displayApplicantName(session.applicantFullName)} />
            <KeyValueRow label="Email:" value={session.inviteEmail || 'NA'} />
            <KeyValueRow
              label="Contact Number:"
              value={formatMobile(session.applicantMobile) ?? 'NA'}
            />
          </KeyValueList>
        </SubCard>

        <DecisionCard session={session} />
      </TwoColumnGrid>
    </Accordion>
  );
}

/**
 * Palette for the two states of the card below.
 *
 * The orange values are the designer's `.sc-risk-label-banner` verbatim
 * (SessionDetailContent.css). They have no GREEN variant of this card — the clean run was
 * never drawn — so rather than inventing a green, the success side reuses the success
 * colours they do define, on `.sc-risk-radio--approve` in ConfigTabs.css: `#00A92C` on
 * `#E8F5E9`. The one derived value is the pill's lighter gradient stop, lightened from
 * `#00A92C` the same way their `#FF7426 → #FF9E67` is, so the two cards read as siblings
 * rather than as two unrelated designs.
 */
const WARNING_TONE = {
  risk: {
    border: 'rgba(242,115,16,0.5)',
    cardBackground: 'linear-gradient(180deg, rgba(248,172,120,0.2) 0%, rgba(255,255,255,0.2) 100%)',
    pillBackground: 'linear-gradient(0deg, #FF7426 0%, #FF9E67 84.6%)',
    itemIcon: '#FF7426',
    pillLabel: 'Risk Label Found',
  },
  clear: {
    border: 'rgba(0,169,44,0.5)',
    cardBackground: 'linear-gradient(180deg, rgba(232,245,233,0.9) 0%, rgba(255,255,255,0.2) 100%)',
    pillBackground: 'linear-gradient(0deg, #00A92C 0%, #4FD07A 84.6%)',
    itemIcon: '#00A92C',
    pillLabel: 'No Risk Found',
  },
  // Neutral, NOT green. A run where nothing has been checked yet also produces zero
  // warnings, and painting that as "No Risk Found" would be the card's one chance to
  // mislead: it would tell a reviewer a session had come back clean when in fact it had not
  // been looked at. Grey says "nothing to report yet", which is the truth.
  pending: {
    border: 'rgba(148,163,184,0.5)',
    cardBackground: 'linear-gradient(180deg, rgba(241,245,249,0.9) 0%, rgba(255,255,255,0.2) 100%)',
    pillBackground: 'linear-gradient(0deg, #64748B 0%, #94A3B8 84.6%)',
    itemIcon: '#64748B',
    pillLabel: 'Not Checked Yet',
  },
} as const;

/**
 * Warnings — ALWAYS rendered, whether or not the run has any.
 *
 * It used to be hidden on a clean run, on the reasoning that an empty box reads as "we
 * failed to load them" rather than "there are none". That reasoning was wrong twice over.
 * The absence carried no information a reviewer could act on — they could not tell a clean
 * run from a card that failed to render — and because this is the FIRST grid item, hiding it
 * moved Session Details from the right column into the left. So the two things a reviewer
 * looks for first sat in different places depending on the session, and the eye had to hunt
 * for them on every row of the queue.
 *
 * Now the layout is fixed: risk on the left, Session Details on the right, every time. A
 * clean run says so explicitly and in green, which is a stronger statement than silence.
 */
function WarningsCard({
  warnings,
  evaluated,
}: {
  warnings: { key: string; text: string }[];
  /** At least one step has run, so "no warnings" is a real result rather than an empty run. */
  evaluated: boolean;
}) {
  const state = warnings.length > 0 ? 'risk' : evaluated ? 'clear' : 'pending';
  const tone = WARNING_TONE[state];
  const Icon = state === 'risk' ? AlertTriangle : state === 'clear' ? ShieldCheck : Clock;

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border p-[clamp(12px,1vw,16px)]"
      style={{ borderColor: tone.border, background: tone.cardBackground }}
    >
      <h4 className="m-0 text-[length:var(--text-16)] font-semibold text-[#041A3A]">Warnings</h4>

      <div
        className="flex max-w-max items-center justify-center gap-2 rounded-full px-8 py-[clamp(8px,0.6vw,10px)] text-[length:var(--text-20)] text-white"
        style={{ background: tone.pillBackground }}
      >
        <Icon size={16} className="shrink-0" />
        <span>{tone.pillLabel}</span>
      </div>

      {state !== 'risk' ? (
        // Says what was checked, not just that nothing was found — "no warnings" alone
        // leaves open whether anything looked.
        <p className="m-0 mt-1 text-[length:var(--text-14)] font-medium leading-snug text-[#303030]">
          {state === 'clear'
            ? 'Every completed step passed its checks. No risk flags were raised and no step needs review.'
            : 'No step has been completed yet, so there is nothing to flag. Warnings appear here as the applicant progresses.'}
        </p>
      ) : (
        <ul className="m-0 mt-1 flex list-none flex-col gap-2.5 p-0">
          {warnings.map((w) => (
            <li
              key={w.key}
              className="flex items-center gap-2.5 text-[length:var(--text-14)] font-medium text-[#303030]"
            >
              <AlertTriangle size={14} className="shrink-0" style={{ color: tone.itemIcon }} />
              <span>{w.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The admin verdict's audit record: who decided, when, how far the run had got at that
 * moment, and why.
 *
 * Rendered only once a verdict exists. There is nothing useful to say about a run no human
 * has judged, and an empty card full of dashes reads as a loading failure.
 *
 * The completion figure is the whole reason this card exists. Without it, an approval taken
 * at 43% is indistinguishable months later from an approval of a finished run — so it is
 * captured at decision time and shown here beside the verdict it qualifies. Verdicts
 * recorded before it was captured show nothing rather than a reconstructed number, which
 * would be a guess written into an audit trail.
 */
function DecisionCard({ session }: { session: SessionDetailDto }) {
  const verdict = session.verifiedStatus;
  if (!verdict || verdict === 'NotApplicable') return null;

  const completion = session.completionPercentAtDecision;

  return (
    <SubCard title="Admin Decision">
      <KeyValueList>
        <KeyValueRow label="Decision:" value={verifiedStatusLabel(verdict)} />
        <KeyValueRow label="Decided By:" value={session.verifiedByName || 'NA'} />
        <KeyValueRow label="Decided At:" value={formatSessionDateTime(session.verifiedAt)} />
        {completion !== null && (
          <KeyValueRow label="Run Completion:" value={`${completion}% at the time`} />
        )}
        <KeyValueRow label="Reason:" value={session.verifiedNote || 'NA'} />
      </KeyValueList>
    </SubCard>
  );
}
